// 游戏模块状态管理

import { createWithEqualityFn } from "zustand/traditional";
import { subscribeWithSelector } from "zustand/middleware";
import { GameState, GameActions } from "../types";

// 初始状态
const initialState: GameState = {
  cache: new Map(),
  paginatedGameLists: new Map(),
  categoryCache: null,
  platformCache: null,
  favoriteGameIds: new Set(),
  favoriteGames: [],
  isFavoriteListLoaded: false,
};

// 创建游戏store
export const useGameStore = createWithEqualityFn<GameState & GameActions>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    // 分页游戏列表缓存管理
    setPaginatedGameList: (categoryId, data) => {
      const newMap = new Map(get().paginatedGameLists);
      newMap.set(categoryId, data);
      set({ paginatedGameLists: newMap });
    },

    getPaginatedGameList: categoryId => {
      return get().paginatedGameLists.get(categoryId);
    },

    setPageData: (categoryId, pageNo, games, pageInfo) => {
      const existing = get().paginatedGameLists.get(categoryId);
      const newMap = new Map(get().paginatedGameLists);

      if (existing) {
        const newPages = new Map(existing.pages);
        newPages.set(pageNo, games);
        newMap.set(categoryId, {
          pages: newPages,
          currentPage: pageInfo.currentPage,
          totalPages: pageInfo.totalPages,
          totalElements: pageInfo.totalElements,
          hasMore: pageInfo.hasMore,
          timestamp: Date.now(),
        });
      } else {
        const newPages = new Map();
        newPages.set(pageNo, games);
        newMap.set(categoryId, {
          pages: newPages,
          currentPage: pageInfo.currentPage,
          totalPages: pageInfo.totalPages,
          totalElements: pageInfo.totalElements,
          hasMore: pageInfo.hasMore,
          timestamp: Date.now(),
        });
      }

      set({ paginatedGameLists: newMap });
    },

    getPageData: (categoryId, pageNo) => {
      const data = get().paginatedGameLists.get(categoryId);
      return data?.pages.get(pageNo);
    },

    // 游戏分类缓存管理
    setGameCategoryCache: data => {
      set({ categoryCache: data });
    },

    getGameCategoryCache: () => {
      return get().categoryCache;
    },

    clearGameCategoryCache: () => {
      set({ categoryCache: null });
    },

    // 游戏平台缓存管理
    setGamePlatformCache: data => {
      set({ platformCache: data });
    },

    getGamePlatformCache: () => {
      return get().platformCache;
    },

    clearGamePlatformCache: () => {
      set({ platformCache: null });
    },

    // 缓存相关方法继承自 BaseModuleActions
    setCache: (key, data, ttl) => {
      const newCache = new Map(get().cache);
      newCache.set(key, {
        data,
        ttl: ttl || 0,
        timestamp: Date.now(),
      });
      set({ cache: newCache });
    },

    getCache: key => {
      const cached = get().cache.get(key);
      if (!cached) return undefined;
      if (cached.ttl && Date.now() - cached.timestamp > cached.ttl) {
        const newCache = new Map(get().cache);
        newCache.delete(key);
        set({ cache: newCache });
        return undefined;
      }
      return cached.data;
    },

    clearCache: pattern => {
      if (!pattern) {
        set({ cache: new Map() });
        return;
      }
      const newCache = new Map(get().cache);
      for (const key of newCache.keys()) {
        if (key.includes(pattern)) {
          newCache.delete(key);
        }
      }
      set({ cache: newCache });

      // 同时清除 paginatedGameLists 中匹配的缓存
      const newPaginatedMap = new Map(get().paginatedGameLists);
      for (const key of newPaginatedMap.keys()) {
        if (key.includes(pattern)) {
          newPaginatedMap.delete(key);
        }
      }
      set({ paginatedGameLists: newPaginatedMap });
    },

    hasCache: key => {
      return get().cache.has(key);
    },

    getCacheKeys: () => {
      return Array.from(get().cache.keys());
    },

    getCacheSize: () => {
      return get().cache.size;
    },

    cacheFirst: async (key, apiCall, ttl) => {
      const cached = get().getCache(key);
      if (cached) return cached;
      const data = await apiCall();
      get().setCache(key, data, ttl);
      return data;
    },

    // 收藏列表管理
    setFavoriteGameIds: gameIds => {
      set({ favoriteGameIds: new Set(gameIds) });
    },

    setFavoriteGames: games => {
      const gameIds = games.map(g => g.id);
      set({
        favoriteGames: games,
        favoriteGameIds: new Set(gameIds),
      });
    },

    addFavoriteGame: gameId => {
      const newSet = new Set(get().favoriteGameIds);
      newSet.add(gameId);
      set({ favoriteGameIds: newSet });
      // 只更新本地状态，不触发 API 刷新
      // isFavoriteListLoaded 保持不变，只有在进入收藏页面时才刷新 API
    },

    removeFavoriteGame: gameId => {
      const newSet = new Set(get().favoriteGameIds);
      newSet.delete(gameId);
      // 同时从完整列表中移除
      const newGames = get().favoriteGames.filter(g => g.id !== gameId);
      set({
        favoriteGameIds: newSet,
        favoriteGames: newGames,
      });
    },

    isFavoriteGame: gameId => {
      return get().favoriteGameIds.has(gameId);
    },

    setFavoriteListLoaded: loaded => {
      set({ isFavoriteListLoaded: loaded });
    },

    getFavoriteGames: () => {
      return get().favoriteGames;
    },
  }))
);
